// App.js - Archivo principal con sistema de login y submenús integrados + WhatsApp
import React, { useState, useRef, useEffect } from "react";
import { AuthProvider } from './contexts/AuthContext';
import { authService } from './services/authService';
import ProtectedRoute from './components/ProtectedRoute';
import TaskDropdown from './components/TaskDropdown';
import FloatingMenu from './components/FloatingMenu';
import PlannerView from './components/PlannerView';
import LiquidacionesView from './components/LiquidacionesView';
import TechniciansView from './components/TechniciansView';
import ZonesView from './components/ZonesView';
import MaterialView from './components/MaterialView';
import AlmacenView from './components/AlmacenView';
// 🆕 IMPORTAR COMPONENTE DE WHATSAPP
import { Icons } from './utils/icons';
import zapataLogo from './assets/zapata-logo.png';
import './components/HeaderButtons.css';
import { useAuth } from './contexts/AuthContext';
import CrmView from './components/CrmView';
import PegatinasView from './components/PegatinasView';
import KanbanComercialView from './components/KanbanComercialView';
import DashboardView from './components/DashboardView';
import DashboardAtencionCliente from './components/DashboardAtencionCliente';
import SearchModal from './components/SearchModal';
import KeystoreView from './components/KeystoreView';
import CRMEmailView from './components/CRMEmailView';
import UsersView from './components/UsersView';
import TechnicianApp from './components/technician/TechnicianApp';
import FloatingChatIA from './components/FloatingChatIA';
import LeadsView from './components/LeadsView';
import FleetView from './components/FleetView';
import FichajesView from './components/FichajesView';
import MantenimientosView from './components/MantenimientosView';
import CalendarioView from './components/calendario/CalendarioView';
import NotificationBell from './components/calendario/NotificationBell';
import FirmaPublica from './components/FirmaPublica';
import FirmaPresupuesto from './components/FirmaPresupuesto';
import OrdenesView from './components/v2/OrdenesView';
import SeedDataView from './components/v2/SeedDataView';
import MigracionView from './components/v2/MigracionView';
import FacturasView from './components/v2/FacturasView';
import InformesView from './components/v2/InformesView';
import ProveedoresView from './components/v2/ProveedoresView';
import PresupuestosView from './components/v2/PresupuestosView';
import InstalacionesView from './components/v2/InstalacionesView';
import CalculadoraConductosView from './components/v2/CalculadoraConductosView';
import ScriptEditorView from './components/admin/ScriptEditorView';
import MenuAdminView from './components/admin/MenuAdminView';
import TecnicosAdminView from './components/admin/TecnicosAdminView';
import ErroresAdminView from './components/admin/ErroresAdminView';
import PiezasPendientesAdminView from './components/admin/PiezasPendientesAdminView';
import CatalogoPiezasAdminView from './components/admin/CatalogoPiezasAdminView';
import EmailsAdminView from './components/admin/EmailsAdminView';
import ErrorBoundary from './components/ErrorBoundary';
import { hydrateTecnicos } from './services/tecnicosService';
import ConfigEmpresaView from './components/v2/ConfigEmpresaView';
import { capturePageview } from './lib/analytics';


const ROLE_LABEL = {
  admin:                   'Administrador',
  coordinador_operaciones: 'Director de Operaciones',
  atencion_cliente:        'Atención al Cliente',
  tecnico:                 'Técnico',
};

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const EMPTY_PROFILE_FORM = { name: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '' };

const HeaderWithButtons = ({ onSearchClick }) => {
  const { logout, user, updateUser, enterTechnicianMode } = useAuth();
  const [showTasks,   setShowTasks]   = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfile,  setShowProfile]  = useState(false);
  const [form,         setForm]         = useState(EMPTY_PROFILE_FORM);
  const [formError,    setFormError]    = useState('');
  const [formSuccess,  setFormSuccess]  = useState('');
  const [saving,       setSaving]       = useState(false);
  const dropdownRef = useRef(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openProfile = () => {
    setForm({ name: user?.name || '', email: user?.email || '', currentPassword: '', newPassword: '', confirmPassword: '' });
    setFormError('');
    setFormSuccess('');
    setShowDropdown(false);
    setShowProfile(true);
  };

  const handleLogout = () => {
    setShowDropdown(false);
    if (window.confirm('¿Estás seguro de que quieres cerrar sesión?')) logout();
  };

  // Tema claro/oscuro: aplica al instante (updateUser dispara el efecto en
  // AuthContext que pone/quita la clase `dark`) y se guarda en segundo
  // plano — no requiere contraseña, a diferencia del resto del formulario.
  const handleSetTheme = async (theme) => {
    updateUser({ theme });
    try {
      await fetch('/.netlify/functions/updateTheme', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ email: user.email, theme }),
      });
    } catch (e) { console.error('[updateTheme]', e.message); }
  };

  const handleSaveProfile = async () => {
    if (!form.currentPassword) return setFormError('La contraseña actual es obligatoria');
    if (form.newPassword && form.newPassword !== form.confirmPassword) return setFormError('Las contraseñas nuevas no coinciden');
    if (form.newPassword && form.newPassword.length < 6) return setFormError('La nueva contraseña debe tener al menos 6 caracteres');

    setSaving(true);
    setFormError('');
    setFormSuccess('');
    try {
      const body = {
        email:           user.email,
        currentPassword: form.currentPassword,
        name:            form.name    || undefined,
        newEmail:        form.email !== user.email ? form.email : undefined,
        newPassword:     form.newPassword || undefined,
      };

      const resp = await fetch('/.netlify/functions/updateProfile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error desconocido');

      updateUser({ name: data.user.name, email: data.user.email });
      setFormSuccess('Perfil actualizado correctamente');
      setForm(f => ({ ...f, currentPassword: '', newPassword: '', confirmPassword: '' }));
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed top-4 right-4 z-50">
        <div className="bg-white rounded-lg shadow-lg p-4 flex items-center gap-4">

          {/* Búsqueda global */}
          <button onClick={onSearchClick} className="task-button" title="Búsqueda global (Ctrl+K)">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          {/* Tareas */}
          <button onClick={() => setShowTasks(s => !s)} className="task-button" title="Ver tareas pendientes">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </button>

          {/* Notificaciones del calendario interno */}
          {['admin', 'coordinador_operaciones', 'atencion_cliente'].includes(user?.role) && <NotificationBell />}

          {/* Avatar + desplegable de perfil */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(s => !s)}
              className="flex items-center gap-2.5 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                {getInitials(user?.name)}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-sm font-semibold text-gray-800 leading-tight">{user?.name}</p>
                <p className="text-xs text-gray-400 leading-tight">{ROLE_LABEL[user?.role] || user?.role}</p>
              </div>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {/* Cabecera del dropdown */}
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {getInitials(user?.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Opciones */}
                <div className="p-1.5">
                  <button
                    onClick={openProfile}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Mi perfil
                  </button>

                  {user?.role === 'admin' && (
                    <>
                      <button
                        onClick={() => { setShowDropdown(false); enterTechnicianMode(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-left"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Cambiar a Técnico
                      </button>
                      <div className="my-1 border-t border-gray-100" />
                    </>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Cerrar sesión
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dropdown de Tareas */}
      <TaskDropdown isOpen={showTasks} onClose={() => setShowTasks(false)} />

      {/* Modal de perfil */}
      {showProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                {getInitials(user?.name)}
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Mi perfil</h3>
                <p className="text-xs text-gray-400">{ROLE_LABEL[user?.role] || user?.role}</p>
              </div>
              <button onClick={() => setShowProfile(false)} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">{formError}</div>
              )}
              {formSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-3 py-2 text-sm">✓ {formSuccess}</div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Nombre completo</label>
                <input type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                <input type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                />
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Tema</p>
                <div className="flex gap-2">
                  {[['light', 'Claro', Icons.Sun], ['dark', 'Oscuro', Icons.Moon]].map(([value, label, Icon]) => (
                    <button key={value} type="button" onClick={() => handleSetTheme(value)}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-bold rounded-xl border transition-colors ${
                        (user?.theme || 'light') === value ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                      <Icon className="w-4 h-4" />{label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Cambiar contraseña</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Contraseña actual <span className="text-red-500">*</span></label>
                    <input type="password" value={form.currentPassword} placeholder="Requerida para guardar cambios"
                      onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1.5">Nueva contraseña <span className="text-gray-400">(opcional)</span></label>
                    <input type="password" value={form.newPassword} placeholder="Dejar vacío para no cambiarla"
                      onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                    />
                  </div>
                  {form.newPassword && (
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">Confirmar nueva contraseña</label>
                      <input type="password" value={form.confirmPassword} placeholder="Repite la nueva contraseña"
                        onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button onClick={() => setShowProfile(false)}
                className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
                Cancelar
              </button>
              <button onClick={handleSaveProfile} disabled={saving}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors">
                {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Guardar cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Vista inicial según rol — atención al cliente empieza directamente en el CRM
const getDefaultView = (role) => {
  if (role === 'atencion_cliente') return 'incidence';
  return 'dashboard';
};

function AppContent() {
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Recuerda la vista actual en sessionStorage — sin esto, cada F5 remonta
  // AppContent y currentView vuelve a su valor por defecto (Dashboard),
  // perdiendo dónde estaba el usuario. sessionStorage (no localStorage) para
  // que se limpie sola al cerrar la pestaña y en logout (ver AuthContext).
  const [currentView, setCurrentView] = useState(() => sessionStorage.getItem('zapata_current_view') || getDefaultView(user?.role));
  const [showSearch, setShowSearch] = useState(false);
  const [contratoCliente, setContratoCliente] = useState(null);
  // Pestaña inicial al navegar a "Órdenes de trabajo" desde otro sitio (p.ej.
  // "Ver rutas →" del Dashboard, que quiere aterrizar en Analítica) — null =
  // la propia OrdenesView decide su valor por defecto ('todas').
  const [ordenesInitialTab, setOrdenesInitialTab] = useState(null);

  React.useEffect(() => {
    sessionStorage.setItem('zapata_current_view', currentView);
  }, [currentView]);

  // No hay React Router — currentView es la única señal de "pantalla
  // activa", así que el pageview de PostHog se manda a mano aquí.
  React.useEffect(() => {
    capturePageview(currentView);
  }, [currentView]);

  // Hidrata la cache del roster de técnicos (colección `tecnicos`, con fallback
  // a constants) para los consumidores no reactivos.
  React.useEffect(() => {
    if (user) hydrateTecnicos().catch(() => {});
  }, [user]);

  // Ctrl+K / Cmd+K abre búsqueda global
  React.useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(s => !s);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSelectView = (viewId, data) => {
    if (viewId === 'guia') {
      window.open('/guia-usuario.html', '_blank', 'noopener,noreferrer');
      return;
    }
    if (viewId === 'v2-ordenes') setOrdenesInitialTab(data?.tab || null);
    console.log('Vista seleccionada:', viewId);
    setCurrentView(viewId);
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return user?.role === 'atencion_cliente'
          ? <DashboardAtencionCliente onNavigate={handleSelectView} />
          : <DashboardView onNavigate={handleSelectView} />;
      case 'planner':
        return <PlannerView />;
      case 'incidence':
        return <CrmView initialTab="nuevo" onNavigate={(viewId, data) => {
          if (viewId === 'mantenimientos' && data) setContratoCliente(data);
          setCurrentView(viewId);
        }} />;
      case 'calendario':
        return <CalendarioView />;
      case 'pegatinas':
        return <PegatinasView />;
      case 'kanbanComercial':
        return <KanbanComercialView />;
      case 'keystore':
        return <KeystoreView />;
      case 'crm-email':
        return <CRMEmailView onNavigate={handleSelectView} />;
      case 'leads':
        return <LeadsView />;
      case 'flota':
        return <FleetView />;
      case 'fichajes':
        return <FichajesView />;
      case 'mantenimientos':
        return <MantenimientosView clienteInicial={contratoCliente} onClienteInicialConsumed={() => setContratoCliente(null)} />;
      case 'users':
        return <UsersView />;
      case 'liquidaciones':
        return <LiquidacionesView />;
      case 'material':
      case 'material-pedidos':
        return <MaterialView />;
      case 'almacen':
      case 'v2-almacen':
        return <AlmacenView />;
      case 'technicians':
        return <TechniciansView />;
      case 'zones':
        return <ZonesView />;
      // "Rutas" y "Comisiones" se movieron dentro de "Órdenes de trabajo"
      // (Z v2.0) como pestañas más (mismo criterio que Agenda Semanal) — ya
      // no son entradas de menú aparte. 2026-09-16.
      // ── Z v2.0 ─────────────────────────────────────────────────────────────
      case 'v2-clientes':
        return <CrmView initialTab="clientes" onNavigate={(viewId, data) => {
          if (viewId === 'mantenimientos' && data) setContratoCliente(data);
          setCurrentView(viewId);
        }} />;
      case 'v2-ordenes':
        return <OrdenesView initialTab={ordenesInitialTab} onNavigate={(viewId) => setCurrentView(viewId)} />;
      case 'v2-seed':
        return <SeedDataView />;
      case 'v2-facturas':
        return <FacturasView />;
      case 'v2-informes':
        return <InformesView />;
      case 'v2-migracion':
        return <MigracionView />;
      case 'v2-proveedores':
        return <ProveedoresView />;
      case 'v2-presupuestos':
        return <PresupuestosView />;
      case 'v2-instalaciones':
        return <InstalacionesView />;
      case 'v2-bancos':
        // Bancos ahora es una pestaña de Facturas (mismo componente,
        // conciliación con facturas + su propio dashboard) — se mantiene
        // este punto de menú aparte por si acaso, abriendo directo esa
        // pestaña, mismo criterio que 'v2-clientes' → CrmView.
        return <FacturasView initialTab="bancos" />;
      case 'v2-conductos':
        return <CalculadoraConductosView />;
      case 'config-empresa':
        return <ConfigEmpresaView />;
      case 'scripts-admin':
        return <ScriptEditorView />;
      case 'menu-admin':
        return user?.role === 'admin' ? <MenuAdminView /> : null;
      case 'tecnicos-admin':
        return <TecnicosAdminView />;
      case 'errores-admin':
        return user?.role === 'admin' ? <ErroresAdminView /> : null;
      case 'piezas-pendientes-admin':
        return ['admin', 'coordinador_operaciones'].includes(user?.role) ? <PiezasPendientesAdminView /> : null;
      case 'catalogo-piezas-admin':
        return ['admin', 'coordinador_operaciones'].includes(user?.role) ? <CatalogoPiezasAdminView /> : null;
      case 'emails-admin':
        return user?.role === 'admin' ? <EmailsAdminView /> : null;
      default:
        return <PlannerView />;
    }
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'dashboard':
        return '';
      case 'planner':
        return 'Sistema de gestión de citas';
      case 'calendario':
        return 'Reuniones, tareas y huecos del equipo';
      case 'pegatinas':
        return 'Generador de pegatinas de ordenes';
      case 'kanbanComercial':
        return 'Pipeline de presupuestos y comercial';
      case 'liquidaciones':
        return 'Gestión de liquidaciones pendientes';
      case 'material':
      case 'material-pedidos':
        return 'Gestión de pedidos de material';
      case 'almacen':
      case 'v2-almacen':
        return 'Almacén — gestión de stock y productos';
      case 'technicians':
        return 'Gestión de recursos humanos';
      case 'zones':
        return 'Distribución territorial';
      case 'keystore':
        return 'Contraseñas y credenciales de la empresa';
      case 'crm-email':
        return 'Bandeja de entrada compartida del equipo';
      case 'leads':
        return 'Leads de campañas de Facebook Ads';
      case 'flota':
        return 'Gestión de flota de vehículos';
      case 'fichajes':
        return 'Control horario de técnicos — RDL 8/2019';
      case 'mantenimientos':
        return 'MANTENIMIENTOS';
      case 'v2-clientes':
      case 'incidence':
        return 'CRM — centro de control de clientes que contactan';
      case 'v2-ordenes':
        return 'OPERACIONES';
      case 'v2-seed':
        return 'Z v2.0 — Panel de datos de prueba';
      case 'v2-facturas':
        return 'Z v2.0 — Facturas, bancos y analítica financiera';
      case 'v2-informes':
        return 'Z v2.0 — Informes y Analítica';
      case 'v2-migracion':
        return 'Z v2.0 — Migración StelOrder → Firebase';
      case 'v2-proveedores':
        return 'Z v2.0 — Proveedores y alertas de stock';
      case 'v2-presupuestos':
        return 'Z v2.0 — Presupuestos digitales';
      case 'v2-instalaciones':
        return 'Z v2.0 — Instalaciones por fases';
      case 'v2-bancos':
        return 'Z v2.0 — Facturas, bancos y analítica financiera';
      case 'v2-conductos':
        return 'Z v2.0 — Calculadora de conductos';
      case 'config-empresa':
        return 'Configuración de empresa — datos fiscales y bancarios';
      case 'scripts-admin':
        return 'Scripts CRM — configuración de guías de atención al cliente';
      case 'menu-admin':
        return 'Editor de menú — visibilidad y agrupación por rol';
      case 'tecnicos-admin':
        return 'Técnicos — roster, colores y zonas por día';
      case 'errores-admin':
        return 'Errores de la aplicación — observabilidad';
      case 'piezas-pendientes-admin':
        return 'Piezas pendientes de alta — catálogo de StelOrder';
      case 'catalogo-piezas-admin':
        return 'Catálogo de piezas por modelo — sugerencias de técnicos';
      case 'emails-admin':
        return 'Correos a clientes — registro de envíos';
      default:
        return 'Sistema de gestión de citas';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      {/* Header con botones */}
      <HeaderWithButtons onSearchClick={() => setShowSearch(true)} />

      {/* Búsqueda global */}
      {showSearch && <SearchModal onClose={() => setShowSearch(false)} onNavigate={handleSelectView} />}

      {/* Menú flotante */}
      <FloatingMenu
        isOpen={isMenuOpen}
        onToggle={toggleMenu}
        onSelectView={handleSelectView}
        userRole={user?.role}
      />

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto mt-4">
        {/* Header dinámico */}
        <div className="text-center mb-8">
          <p className="text-lg text-gray-600">
            {getViewTitle()}
          </p>
        </div>

        {/* Vista actual — aislada: si una vista revienta, el menú y la
            cabecera siguen usables y se puede navegar a otra. */}
        <ErrorBoundary name={`view:${currentView}`} resetKey={currentView} compact>
          {renderCurrentView()}
        </ErrorBoundary>
      </div>

      {/* Chat IA flotante */}
      <FloatingChatIA />

      {/* Logo en esquina inferior izquierda */}
      <div className="fixed bottom-6 left-6 z-40">
        <img 
          src={zapataLogo} 
          alt="Climatización Zapata Logo" 
          className="h-20 w-20 object-contain drop-shadow-lg hover:scale-105 transition-transform duration-200 opacity-80 hover:opacity-100"
        />
      </div>
    </div>
  );
}

function ModeSelectionScreen({ onSelect, userName }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-red-900 flex flex-col items-center justify-center p-6">
      <div className="mb-8 text-center">
        <img src={zapataLogo} alt="Climatización Zapata" className="h-20 w-20 object-contain mx-auto mb-4 drop-shadow-xl" />
        <p className="text-white/70 text-sm">Bienvenido, <span className="text-white font-semibold">{userName}</span></p>
        <h1 className="text-white font-bold text-2xl mt-1">¿Cómo quieres entrar?</h1>
      </div>

      <div className="w-full max-w-sm space-y-4">
        <button
          onClick={() => onSelect('director')}
          className="w-full bg-white hover:bg-gray-50 active:bg-gray-100 rounded-2xl p-5 text-left shadow-xl transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base">Director</p>
              <p className="text-gray-500 text-sm">Panel de control, planificador, rutas y gestión</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => onSelect('tecnico')}
          className="w-full bg-white hover:bg-gray-50 active:bg-gray-100 rounded-2xl p-5 text-left shadow-xl transition-all group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-gray-900 text-base">Técnico</p>
              <p className="text-gray-500 text-sm">Ver órdenes del día, ruta y comisiones</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

function AppRouter() {
  const { user, enterTechnicianMode } = useAuth();
  const [adminModeChosen, setAdminModeChosen] = useState(
    () => !!sessionStorage.getItem('zapata_admin_mode')
  );

  const handleSelectMode = (mode) => {
    if (mode === 'tecnico') enterTechnicianMode();
    else sessionStorage.setItem('zapata_admin_mode', 'director');
    setAdminModeChosen(true);
  };

  if (user?.role === 'tecnico') return <TechnicianApp />;
  if (user?.role === 'admin' && !adminModeChosen) {
    return <ModeSelectionScreen onSelect={handleSelectMode} userName={user.name} />;
  }
  // ph-mask: la grabación de PostHog tapa todo el texto de oficina (CRM,
  // clientes, mantenimientos... con datos de cliente en cualquier sitio) —
  // la app del técnico ya solo tapa lo marcado, ver analytics.js. `contents`
  // para que el envoltorio no altere el layout.
  return <div className="ph-mask contents"><AppContent /></div>;
}

function App() {
  // Public routes: render without auth wrapper. Si añades una ruta pública
  // nueva aquí, añádela TAMBIÉN a PUBLIC_PATH_PREFIXES en
  // src/utils/publicRoutes.js — esa lista es la que usa analytics.js para
  // no iniciar PostHog nunca en una ruta pública (grabó sesiones reales de
  // cliente en /firmar/ por no comprobar esto, Rafa 2026-11-05).
  const path = window.location.pathname;
  if (path.startsWith('/firma-presupuesto/')) {
    const token = path.split('/').filter(Boolean).pop();
    return <FirmaPresupuesto token={token} />;
  }
  if (path.startsWith('/firmar/')) {
    const token = path.split('/').filter(Boolean).pop();
    return <FirmaPublica token={token} />;
  }
  return (
    <AuthProvider>
      <ProtectedRoute>
        <AppRouter />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;